/**
 * QQ management over the live E2E relay (v0.5.0) — pure, testable helpers.
 *
 * AppState uses these so the live path validates daemon responses exactly like
 * the loopback `/qq/*` client: a malformed `qq.response` is turned into a real
 * error (never a partial/fake status), and request failures surface the daemon's
 * fixed error message + code verbatim.
 */
import type { QqManagementOp } from '@luminis/protocol';

import {
  qqPermissionsBody,
  qqStatusFromUnknown,
  type QqPermissionsPatch,
  type QqStatus,
} from '../qq';

export interface QqLiveResult {
  ok: boolean;
  data?: unknown;
  error?: { code: string; message: string };
}

/** Availability of the QQ management card for the current transport. */
export interface QqLiveAvailability {
  available: boolean;
  reason?: string;
}

/**
 * Decide whether the QQ card may call the daemon. Demo mode keeps the mock
 * client; live mode needs a paired device + an enabled E2E relay plan. A
 * temporarily offline relay stays "available": the request path surfaces the
 * real error instead of hiding the card.
 */
export function qqLiveAvailability(input: {
  hasClient: boolean;
  demoMode: boolean;
  paired: boolean;
  relayEnabled: boolean;
  relayReason?: string;
}): QqLiveAvailability {
  if (input.hasClient) return { available: true };
  if (input.demoMode) {
    return { available: false, reason: '演示模式尚未初始化本地设备（请先完成配对）' };
  }
  if (!input.paired) {
    return { available: false, reason: 'QQ 管理需要通过已配对的中继访问 daemon（请先配对）' };
  }
  if (!input.relayEnabled) {
    return {
      available: false,
      reason: `live relay 未就绪，无法访问 daemon 的 QQ 管理接口：${input.relayReason ?? '未配置'}`,
    };
  }
  return { available: true };
}

/** Injected transport: `LiveRelayController.requestQq` in production. */
export type QqLiveRequester = (
  op: QqManagementOp,
  body?: Record<string, unknown>,
) => Promise<QqLiveResult>;

/** Fixed-message error text for a failed daemon response (code is appended). */
export function qqLiveRequestError(result: QqLiveResult): Error {
  const message = result.error?.message ?? 'QQ 管理请求失败';
  return new Error(
    result.error?.code === undefined ? message : `${message}（${result.error.code}）`,
  );
}

async function requireOkData(
  request: QqLiveRequester,
  op: QqManagementOp,
  body?: Record<string, unknown>,
): Promise<unknown> {
  const result = await request(op, body);
  if (!result.ok) throw qqLiveRequestError(result);
  return result.data;
}

/** `qq.request(status)` -> validated `QqStatus`. */
export async function requestQqStatusLive(request: QqLiveRequester): Promise<QqStatus> {
  const status = qqStatusFromUnknown(await requireOkData(request, 'status'));
  if (status === null) throw new Error('daemon /qq/status 返回了意外数据');
  return status;
}

/** `qq.request(permissions)` -> validated updated `QqStatus`. */
export async function requestQqPermissionsLive(
  request: QqLiveRequester,
  patch: QqPermissionsPatch,
): Promise<QqStatus> {
  const status = qqStatusFromUnknown(
    await requireOkData(request, 'permissions', qqPermissionsBody(patch)),
  );
  if (status === null) throw new Error('daemon /qq/permissions 返回了意外数据');
  return status;
}

/** `qq.request(disconnect)` -> `{ ok: true }` only after the daemon confirms. */
export async function requestQqDisconnectLive(request: QqLiveRequester): Promise<{ ok: true }> {
  const data = await requireOkData(request, 'disconnect');
  const acknowledged =
    data !== null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    (data as Record<string, unknown>)['ok'] === true;
  if (!acknowledged) throw new Error('daemon 未确认断开');
  return { ok: true };
}
